import React, { useState, useRef, useEffect } from 'react';
import { X, FileText, Users, Eye, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import './BriefDetailModal.css';

function BriefDetailModal({ brief, isOpen, onClose, onViewBrief, onAddScript, onDeleteBrief, onScriptClick, onAddScriptToBrief, onCreatorClick, onAddCreatorToBrief }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Helper function to calculate script cost
  const calculateScriptCost = (script) => {
    const settings = script.settings || {};
    const additionalHooks = settings.additionalHooks || 0;
    const additionalCTAs = settings.additionalCTAs || 0;
    
    // Base cost is 1 credit, additional hooks/CTAs are 0.25 credits each
    const baseCost = 1;
    const additionalCost = (additionalHooks + additionalCTAs) * 0.25;
    const totalCost = baseCost + additionalCost;
    
    return {
      baseCost,
      additionalCost,
      totalCost,
      additionalHooks,
      additionalCTAs
    };
  };
  
  // Script scroll state
  const [canScrollLeftScripts, setCanScrollLeftScripts] = useState(false);
  const [canScrollRightScripts, setCanScrollRightScripts] = useState(false);
  const scriptsGridRef = useRef(null);
  

  // Script scroll functions
  const checkScriptScrollButtons = () => {
    if (!scriptsGridRef.current || !brief?.scripts) return;
    
    const filteredScripts = brief.scripts.filter(script => {
      const isMockScript = script.name === 'Script 1' && script.id === 1;
      const hasContent = script.lines && script.lines.length > 0;
      return !isMockScript && hasContent;
    });
    
    if (filteredScripts.length === 0) {
      setCanScrollLeftScripts(false);
      setCanScrollRightScripts(false);
      return;
    }
    
    // Always check if content is actually scrollable, regardless of count
    const { scrollLeft, scrollWidth, clientWidth } = scriptsGridRef.current;
    const hasScrollableContent = scrollWidth > clientWidth + 1; // Add 1px tolerance for rounding
    
    setCanScrollLeftScripts(scrollLeft > 0 && hasScrollableContent);
    setCanScrollRightScripts(scrollLeft < scrollWidth - clientWidth - 1 && hasScrollableContent);
  };

  const scrollScripts = (direction) => {
    if (scriptsGridRef.current) {
      const isMobile = window.innerWidth < 768;
      const cardWidth = isMobile ? 180 : 200;
      const scrollAmount = cardWidth + 16; // Width of one script card + gap (1rem = 16px)
      const newScrollLeft = scriptsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      
      scriptsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };


  // Check scroll buttons when modal opens or scripts change
  useEffect(() => {
    if (isOpen && brief) {
      // Multiple checks to ensure DOM is fully updated
      const checkScroll = () => {
        checkScriptScrollButtons();
      };
      
      // Immediate check
      checkScroll();
      
      // Check after a short delay
      setTimeout(checkScroll, 100);
      
      // Check after a longer delay to catch any layout shifts
      setTimeout(checkScroll, 300);
    }
  }, [isOpen, brief?.scripts]);

  // Check scroll buttons on resize and scroll
  useEffect(() => {
    const handleResize = () => {
      checkScriptScrollButtons();
    };
    
    const handleScriptScroll = () => {
      checkScriptScrollButtons();
    };
    
    window.addEventListener('resize', handleResize);
    
    const scriptsGrid = scriptsGridRef.current;
    
    if (scriptsGrid) {
      scriptsGrid.addEventListener('scroll', handleScriptScroll);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (scriptsGrid) {
        scriptsGrid.removeEventListener('scroll', handleScriptScroll);
      }
    };
  }, [brief?.scripts]);

  if (!isOpen || !brief) return null;

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (onDeleteBrief) {
        await onDeleteBrief(brief);
      }
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting brief:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleAddScriptToBrief = () => {
    if (onAddScriptToBrief && brief) {
      onAddScriptToBrief(brief);
    }
  };

  const handleCreatorClick = (creator) => {
    if (onCreatorClick) {
      onCreatorClick(creator);
    }
  };

  const handleAddCreatorToBrief = () => {
    if (onAddCreatorToBrief) {
      onAddCreatorToBrief(brief);
    }
  };

  return (
    <div className="brief-detail-modal-overlay" onClick={onClose}>
      <div className="brief-detail-modal-container brief-detail-modal-namespace" onClick={(e) => e.stopPropagation()}>
        {/* Top Fold */}
        <div className="brief-detail-modal-top-fold">
          <button className="brief-detail-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Header */}
        <div className="brief-detail-modal-header">
          <div className="brief-detail-modal-title-section">
            <h3 className="brief-detail-modal-title">
              {brief.name || brief.title || brief.briefName || brief.briefTitle || 'Untitled Brief'}
            </h3>
            <div className="brief-detail-modal-divider"></div>
            <p className="brief-detail-modal-description">
              {brief.aiResponse ? (() => {
                // Parse AI response to extract briefOverview section
                const sections = {};
                const lines = brief.aiResponse.split('\n');
                let currentSection = '';
                let currentContent = [];
                
                lines.forEach(line => {
                  if (line.startsWith('## ')) {
                    if (currentSection) {
                      sections[currentSection] = currentContent.join('\n').trim();
                    }
                    currentSection = line.replace('## ', '').trim();
                    currentContent = [];
                  } else {
                    currentContent.push(line);
                  }
                });
                
                if (currentSection) {
                  sections[currentSection] = currentContent.join('\n').trim();
                }
                
                return sections['QUICK BRIEF DESCRIPTION'] || sections['1. BRIEF OVERVIEW'] || sections['BRIEF OVERVIEW'] || sections['briefOverview'] || brief.manualBriefSections?.quickBriefDescription || brief.briefOverview || brief.overview || brief.content || brief.summary || brief.description || 'No overview available';
              })() : (brief.manualBriefSections?.quickBriefDescription || brief.briefOverview || brief.overview || brief.content || brief.summary || brief.description || 'No overview available')}
            </p>
            <button className="brief-detail-view-button" onClick={onViewBrief}>
              <Eye size={16} />
              View Brief
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="brief-detail-modal-content">
          {/* Scripts Section */}
          <div className="brief-detail-section">
            <div className="brief-detail-section-header">
              <h4 className="brief-detail-section-title">Scripts</h4>
              <span className="brief-detail-section-count">
                {brief.scripts ? brief.scripts.filter(script => {
                  const isMockScript = script.name === 'Script 1' && script.id === 1;
                  const hasContent = script.lines && script.lines.length > 0;
                  return !isMockScript && hasContent;
                }).length : 0} scripts
              </span>
              <button 
                className="brief-detail-add-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddScriptToBrief();
                }}
              >
                <Plus size={14} />
                Add Script
              </button>
            </div>
            <div className="brief-detail-scripts-container">
              {/* Script Scroll Controls - Positioned before grid */}
              {brief.scripts && brief.scripts.filter(script => {
                const isMockScript = script.name === 'Script 1' && script.id === 1;
                const hasContent = script.lines && script.lines.length > 0;
                return !isMockScript && hasContent;
              }).length > 0 && (
                <div className="brief-detail-scripts-scroll-controls">
                  {canScrollLeftScripts && (
                    <button 
                      className="brief-detail-scripts-scroll-btn left"
                      onClick={() => scrollScripts('left')}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  )}
                  {canScrollRightScripts && (
                    <button 
                      className="brief-detail-scripts-scroll-btn right"
                      onClick={() => scrollScripts('right')}
                    >
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              )}
              <div 
                className="brief-detail-scripts-grid" 
                ref={scriptsGridRef} 
                onScroll={checkScriptScrollButtons}
              >
                {brief.scripts && brief.scripts.length > 0 ? (
                  brief.scripts
                    .filter(script => {
                      // Filter out mock "Script 1" entries and empty scripts
                      const isMockScript = script.name === 'Script 1' && script.id === 1;
                      const hasContent = script.lines && script.lines.length > 0;
                      return !isMockScript && hasContent;
                    })
                    .sort((a, b) => {
                      // Sort by creation date, newest first (most recent)
                      const dateA = new Date(a.createdAt || a.created || 0);
                      const dateB = new Date(b.createdAt || b.created || 0);
                      return dateB - dateA;
                    })
                    .map((script, index) => (
                      <div 
                        key={script.id || index} 
                        className="brief-detail-script-card"
                        onClick={() => onScriptClick && onScriptClick(script)}
                        style={{ cursor: onScriptClick ? 'pointer' : 'default' }}
                      >
                        <div className="brief-detail-script-card-header">
                          <h6 className="brief-detail-script-card-title">
                            {script.name || script.title || `Script ${script.id || index + 1}`}
                          </h6>
                          {script.isDraft && (
                            <span className="brief-detail-script-draft-badge">Draft</span>
                          )}
                        </div>
                        <div className="brief-detail-script-card-meta">
                          <div className="brief-detail-script-meta-item platform-info">
                            <img 
                              src={`/social icons/${(script.settings?.platform || 'tiktok').toLowerCase()}.svg`} 
                              alt={script.settings?.platform || 'TikTok'} 
                              style={{ width: '12px', height: '12px', marginRight: '4px' }}
                            />
                            <span>{script.settings?.platform || 'TikTok'}</span>
                          </div>
                          <div className="brief-detail-script-meta-item length-info">
                            <span>{script.settings?.videoLength || 30}s</span>
                          </div>
                          {(() => {
                            const costInfo = calculateScriptCost(script);
                            return (
                              <>
                                {(costInfo.additionalHooks > 0 || costInfo.additionalCTAs > 0) && (
                                  <div className="brief-detail-script-meta-item extras-info">
                                    <span className="extras-icon">✨</span>
                                    <span>
                                      {costInfo.additionalHooks > 0 && `+${costInfo.additionalHooks} hooks`}
                                      {costInfo.additionalHooks > 0 && costInfo.additionalCTAs > 0 && ', '}
                                      {costInfo.additionalCTAs > 0 && `+${costInfo.additionalCTAs} CTAs`}
                                    </span>
                                  </div>
                                )}
                                {costInfo.additionalCost > 0 && (
                                  <div className="brief-detail-script-meta-item cost-info">
                                    <span className="cost-amount">
                                      +{costInfo.additionalCost.toFixed(2)} credit{costInfo.additionalCost !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="brief-detail-empty-state">
                    <FileText size={24} />
                    <p>No scripts yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Creators Section */}
          <div className="brief-detail-section">
            <div className="brief-detail-section-header">
              <h4 className="brief-detail-section-title">Creators</h4>
              <span className="brief-detail-section-count">
                {(() => {
                  if (!brief.creators || !Array.isArray(brief.creators)) return 0;
                  // Count creators who have accepted (exclude awaiting_approval, rejected, and completed)
                  return brief.creators.filter(creator => {
                    const status = creator.status;
                    return status && 
                           status !== 'awaiting_approval' &&
                           status !== 'rejected' &&
                           status !== 'completed';
                  }).length;
                })()} creators
              </span>
              <button 
                className="brief-detail-add-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddCreatorToBrief();
                }}
              >
                <Plus size={14} />
                Add Creator
              </button>
            </div>
            <div className="brief-detail-creators-list">
              {brief.creators && brief.creators.length > 0 ? (() => {
                // Filter to only show creators who have accepted (exclude awaiting_approval, rejected, and completed)
                const acceptedCreators = brief.creators.filter(creator => {
                  const status = creator.status;
                  return status && 
                         status !== 'awaiting_approval' &&
                         status !== 'rejected' &&
                         status !== 'completed';
                });

                return acceptedCreators.length > 0 ? (
                  acceptedCreators.map((creator, index) => {
                    const isPro = creator.tier === 'Pro';
                    const creatorClass = isPro ? 'brief-detail-creator-item-pro' : 'brief-detail-creator-item-rising';
                    return (
                      <div 
                        key={creator.projectId || `${creator.id}-${index}`} 
                        className={`brief-detail-creator-item ${creatorClass}`}
                        onClick={() => handleCreatorClick(creator)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="brief-detail-creator-avatar">
                          {creator.avatar || creator.profileImage ? (
                            <img 
                              src={creator.avatar || creator.profileImage} 
                              alt={creator.name || creator.handle || `Creator ${index + 1}`}
                              className="brief-detail-creator-avatar-img"
                              onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                            />
                          ) : (
                            <div className="brief-detail-creator-avatar-placeholder">
                              <Users size={12} />
                            </div>
                          )}
                        </div>
                        <div className="brief-detail-creator-info">
                          <div className="brief-detail-creator-name">
                            {creator.handle 
                              ? (creator.handle.startsWith('@') ? creator.handle : `@${creator.handle}`)
                              : creator.name || `Creator ${index + 1}`}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="brief-detail-empty-state">
                    <Users size={24} />
                    <p>No creators have accepted yet</p>
                  </div>
                );
              })() : (() => {
                const activeCount = brief.creators && Array.isArray(brief.creators) 
                  ? brief.creators.filter(creator => {
                      const status = creator.status;
                      return status && 
                             status !== 'awaiting_approval' &&
                             status !== 'rejected' &&
                             status !== 'completed';
                    }).length
                  : 0;
                return activeCount > 0 ? (
                  <div className="brief-detail-empty-state">
                    <Users size={24} />
                    <p>{activeCount} creators assigned</p>
                  </div>
                ) : (
                  <div className="brief-detail-empty-state">
                    <Users size={24} />
                    <p>No creators assigned</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="brief-detail-modal-footer">
          <button 
            className="brief-detail-delete-button"
            onClick={handleDeleteClick}
            title="Delete brief"
          >
            <Trash2 size={16} />
            Delete Brief
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="brief-delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="brief-delete-confirm-container" onClick={(e) => e.stopPropagation()}>
            <div className="brief-delete-confirm-header">
              <h3>Delete Brief</h3>
            </div>
            <div className="brief-delete-confirm-content">
              <p>Are you sure you want to delete this brief?</p>
              <p className="brief-delete-warning">
                <strong>"{brief.name || brief.title || 'Untitled Brief'}"</strong>
              </p>
              <p className="brief-delete-note">
                This action cannot be undone. All scripts and data associated with this brief will be permanently deleted.
              </p>
            </div>
            <div className="brief-delete-confirm-actions">
              <button 
                className="brief-delete-cancel-button"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="brief-delete-confirm-button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Brief'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BriefDetailModal;







